import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// Přihlášení drží jen httpOnly cookie (withCredentials). Token se do localStorage neukládá – není dostupný pro případný XSS.
const OFFLINE_QUEUEABLE = [
    /^\/jobs\/[^/]+\/costs$/,
    /^\/craftsmen$/,
    /^\/events$/,
    /^\/claims$/,
    /^\/attendance\/check-(in|out)$/,
];

// Obnova relace: po 401 se jednou zavolá /auth/refresh (refresh token v httpOnly cookie, rotace, 30 dní) a požadavek se zopakuje.
let refreshing = null;
const NO_REFRESH = /^\/auth\/(login|refresh|logout|register|accept-invite|reset-password|forgot-password|invite)/;
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

api.interceptors.response.use(null, async (error) => {
    const cfg = error.config;
    if (error.response?.status === 401 && cfg && !cfg._retry && !NO_REFRESH.test(cfg.url || "")) {
        cfg._retry = true;
        try {
            refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
            await refreshing;
            return api(cfg);
        } catch (e) {
            if (e.response?.status === 401 && !/^\/auth\/me/.test(cfg.url || "")) window.dispatchEvent(new Event("auth:expired"));
            return Promise.reject(error);
        }
    }
    const isNetworkError = !error.response || error.response.status === 503;
    if (
        isNetworkError && cfg && cfg.method === "post" && !cfg._replay &&
        typeof cfg.data === "string" &&
        OFFLINE_QUEUEABLE.some((re) => re.test(cfg.url))
    ) {
        const { enqueue } = await import("./offline");
        enqueue({ url: cfg.url, data: cfg.data, ts: Date.now() });
        const { toast } = await import("sonner");
        toast.info("Uloženo offline — synchronizuje se po připojení", { duration: 4000 });
        let payload = {};
        try { payload = JSON.parse(cfg.data); } catch { /* noop */ }
        return Promise.resolve({
            data: { ...payload, id: `offline-${Date.now()}`, _offline: true },
            status: 202,
            config: cfg,
        });
    }
    return Promise.reject(error);
});

export function formatApiError(detail) {
    if (detail == null) return "Něco se pokazilo. Zkuste to znovu.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Soubory a PDF se otevírají přímo – přihlášení zajistí httpOnly cookie (stejná doména), token se do URL nikdy nevkládá.
export function fileUrl(storagePath) {
    return `${API_BASE}/files/${storagePath}`;
}

export function pdfUrl(budgetId) {
    return `${API_BASE}/budgets/${budgetId}/pdf`;
}

export async function downloadFile(path, filename) {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export default api;
